import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import {
  mdiArrowLeft,
  mdiDeleteOutline,
  mdiPencilOutline,
  mdiPlus,
} from "@mdi/js";
import { Button, Chip, Tabs } from "@heroui/react";
import { ROUTES } from "@/constants/routes";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { helpSettingsStore } from "@/stores/HelpSettingsStore";
import type { HelpServiceKind } from "@/pages/HelpPage/types";
import {
  formatHelpExperienceYears,
  formatHelpMoney,
  HELP_PRICING_MODE_LABEL,
  HELP_SERVICE_KIND_COMING_SOON,
  HELP_SERVICE_KIND_LABEL,
  HELP_SERVICE_KIND_OPTIONS,
} from "@/pages/HelpPage/constants";
import ServiceFormDrawer from "./components/ServiceFormDrawer";
import ExecutorFormDrawer from "./components/ExecutorFormDrawer";
import HelpSettingsModals from "./components/HelpSettingsModals";
import "../HelpPage/components/HelpOrderDrawer.css";
import "./HelpSettingsPage.css";

const HelpSettingsPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const {
    tab,
    catalogKind,
    visibleServices,
    executors,
    isLoading,
    isCatalogKindComingSoon,
  } = helpSettingsStore;

  useUnsavedChangesGuard(
    () => helpSettingsStore.isDrawerDirty,
    (onLeave) => helpSettingsStore.requestLeave(onLeave),
  );

  useEffect(() => {
    void helpSettingsStore.loadAll();
    return () => helpSettingsStore.reset();
  }, []);

  const handleBack = () => {
    helpSettingsStore.requestLeave(() => navigate(ROUTES.HELP));
  };

  return (
    <div className="help-settings" data-testid="help-settings-page">
      <div className="help-settings__header">
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label="Назад к заявкам"
          onPress={handleBack}
        >
          <Icon path={mdiArrowLeft} size={0.85} />
        </Button>
        <div>
          <h1 className="help-settings__title">Настройки помощи на дороге</h1>
          <p className="help-settings__subtitle">
            Каталог услуг и исполнителей для заявок помощи на дороге.
          </p>
        </div>
      </div>

      <Tabs
        selectedKey={tab}
        onSelectionChange={(key) => {
          if (!key) return;
          helpSettingsStore.requestTabChange(
            String(key) as "catalog" | "executors",
          );
        }}
        className="help-settings__tabs"
      >
        <Tabs.ListContainer>
          <Tabs.List aria-label="Разделы настроек помощи">
            <Tabs.Tab id="catalog">
              Услуги
              <Tabs.Indicator />
            </Tabs.Tab>
            <Tabs.Tab id="executors">
              Исполнители
              <Tabs.Indicator />
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.ListContainer>

        <Tabs.Panel id="catalog">
          <div className="help-settings__panel">
            <div
              className="help-kind-segment"
              role="tablist"
              aria-label="Тип услуги в каталоге"
            >
              {HELP_SERVICE_KIND_OPTIONS.map((kind) => {
                const isActive = catalogKind === kind;
                return (
                  <button
                    key={kind}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={
                      isActive
                        ? "help-kind-segment__btn help-kind-segment__btn--active"
                        : "help-kind-segment__btn"
                    }
                    onClick={() =>
                      helpSettingsStore.setCatalogKind(kind as HelpServiceKind)
                    }
                  >
                    {HELP_SERVICE_KIND_LABEL[kind]}
                    {HELP_SERVICE_KIND_COMING_SOON[kind] ? (
                      <span className="help-kind-soon">скоро</span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="help-settings__panel-head">
              <p className="help-settings__lead">
                {isCatalogKindComingSoon
                  ? `${HELP_SERVICE_KIND_LABEL[catalogKind]} пока в статусе «скоро».`
                  : "Эти позиции можно добавлять в чек заявки этого типа. Цена в заявке может быть переопределена."}
              </p>
              <Button
                size="sm"
                variant="primary"
                isDisabled={isCatalogKindComingSoon}
                onPress={() => helpSettingsStore.openServiceCreate()}
              >
                <Icon path={mdiPlus} size={0.7} />
                Добавить услугу
              </Button>
            </div>

            {isLoading ? (
              <div className="help-settings__loading">Загрузка...</div>
            ) : visibleServices.length === 0 ? (
              <div className="help-settings__empty">
                {isCatalogKindComingSoon
                  ? "Этот тип услуги пока недоступен."
                  : "Нет услуг. Добавьте первую."}
              </div>
            ) : (
              <ul className="help-service-list">
                {visibleServices.map((service) => (
                  <li key={service.uuid} className="help-service-card">
                    <div className="help-service-card__main">
                      <div className="help-service-card__title-row">
                        <span className="help-service-card__title">
                          {service.title}
                        </span>
                        <Chip size="sm" variant="soft">
                          {HELP_PRICING_MODE_LABEL[service.pricingMode]}
                        </Chip>
                        {service.comingSoon ? (
                          <Chip size="sm" variant="soft">
                            Скоро
                          </Chip>
                        ) : null}
                        {!service.enabled ? (
                          <Chip size="sm" variant="soft" color="warning">
                            Выключена
                          </Chip>
                        ) : null}
                      </div>
                      {service.description ? (
                        <p className="help-service-card__desc">
                          {service.description}
                        </p>
                      ) : null}
                      <p className="help-service-card__meta">
                        {formatHelpMoney(service.unitPrice)}
                        {service.pricingMode === "per_unit" && service.unitLabel
                          ? ` / ${service.unitLabel}`
                          : ""}
                        {service.providerServiceId
                          ? ` · LAT ${service.providerServiceId}`
                          : ""}
                      </p>
                    </div>
                    <div className="help-service-card__actions">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="ghost"
                        aria-label={`Редактировать ${service.title}`}
                        onPress={() => helpSettingsStore.openServiceEdit(service)}
                      >
                        <Icon path={mdiPencilOutline} size={0.75} />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="ghost"
                        aria-label={`Удалить ${service.title}`}
                        onPress={() =>
                          helpSettingsStore.requestDelete({
                            kind: "service",
                            uuid: service.uuid,
                            label: service.title,
                          })
                        }
                      >
                        <Icon path={mdiDeleteOutline} size={0.75} />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Tabs.Panel>

        <Tabs.Panel id="executors">
          <div className="help-settings__panel">
            <div className="help-settings__panel-head">
              <p className="help-settings__lead">
                Справочник для назначения в заявке. В карточке заказа можно
                выбрать из списка или заполнить поля вручную.
              </p>
              <Button
                size="sm"
                variant="primary"
                onPress={() => helpSettingsStore.openExecutorCreate()}
              >
                <Icon path={mdiPlus} size={0.7} />
                Добавить исполнителя
              </Button>
            </div>

            {isLoading ? (
              <div className="help-settings__loading">Загрузка...</div>
            ) : executors.length === 0 ? (
              <div className="help-settings__empty">
                Нет исполнителей. Добавьте первого.
              </div>
            ) : (
              <ul className="help-service-list">
                {executors.map((item) => (
                  <li key={item.uuid} className="help-service-card">
                    <div className="help-service-card__main">
                      <div className="help-service-card__title-row">
                        <span className="help-service-card__title">{item.name}</span>
                        {!item.enabled ? (
                          <Chip size="sm" variant="soft" color="warning">
                            Выключен
                          </Chip>
                        ) : null}
                      </div>
                      <p className="help-service-card__meta">
                        {[item.phone, item.vehicleModel, item.vehiclePlate]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <p className="help-service-card__meta">
                        {item.company}
                        {item.experienceYears
                          ? `${item.company ? " · " : ""}стаж ${formatHelpExperienceYears(item.experienceYears)}`
                          : ""}
                      </p>
                    </div>
                    <div className="help-service-card__actions">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="ghost"
                        aria-label={`Редактировать ${item.name}`}
                        onPress={() => helpSettingsStore.openExecutorEdit(item)}
                      >
                        <Icon path={mdiPencilOutline} size={0.75} />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="ghost"
                        aria-label={`Удалить ${item.name}`}
                        onPress={() =>
                          helpSettingsStore.requestDelete({
                            kind: "executor",
                            uuid: item.uuid,
                            label: item.name,
                          })
                        }
                      >
                        <Icon path={mdiDeleteOutline} size={0.75} />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Tabs.Panel>
      </Tabs>

      <ServiceFormDrawer />
      <ExecutorFormDrawer />
      <HelpSettingsModals />
    </div>
  );
});

HelpSettingsPage.displayName = "HelpSettingsPage";
export default HelpSettingsPage;
